import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import PropTypes from "prop-types";
import React, { useState } from "react";
import ProductDescriptionDetails from "./product-description-details/product-description-details";
import ProductDescriptionHeader from "./product-description-header/product-description-header";
import ProductDescriptionPrice from "./product-description-price/product-description-price";
import Shipping from "../shipping/shipping";
import SVGIcon from "../svg-icon/svg-icon";
import style from "./product-description.css";

function getPromotion(buyOption) {
    let promotions = buyOption["promotions"];
    if (!promotions || promotions.length == 0) {
        return undefined;
    }

    // Only one promo is ever shown to the user at a time
    let promo = promotions[0];
    return {
        tcs: [
            {
                message: gettext("in_stock_product.lyst_promotion_terms_link"),
                url: buyOption["terms_and_conditions_link"]
            },
            {
                message: buyOption["retailer_tc_link_text"],
                url: buyOption["retailer_tc_link"]
            }
        ],
        message: promo["message"],
        url: promo["url"]
    };
}

function ProductDescription({
    absoluteUrl,
    isAmazonProduct,
    isComparePricesEnabled,
    monopriceBuyArea,
    productDescription,
    productHeader,
    expressCheckoutBuyArea
}) {
    const buyArea = monopriceBuyArea || expressCheckoutBuyArea;
    const buyOption = buyArea.buy_option;
    const isLoggedIn = React.useMemo(() => userProfiler.isLoggedIn(), []);
    let promotion = getPromotion(buyOption);

    const [comparePriceOpen, setComparePriceOpen] = useState(false);

    // TODO: When multiple buy options data fixed, add proper store_count
    const comparePriceText = gettext("in_stock_product.compare_prices_in_stores_link", {
        store_count: "4"
    });

    return (
        <div>
            <ProductDescriptionHeader
                absoluteUrl={absoluteUrl}
                className={style.headerArea}
                designerName={productHeader.owner_name}
                designerUrl={productHeader.designer_url}
                isDesignerActive={productHeader.is_owner_active}
                productId={productHeader.product_id}
                shortDescription={productHeader.short_description}
            />
            <ProductDescriptionPrice
                absoluteUrl={absoluteUrl}
                alwaysShowLargeCTA={expressCheckoutBuyArea != null}
                className={style.priceArea}
                currentPriceHtml={buyOption.sale_price_with_currency_symbol_html}
                dateUpdated={buyOption.date_updated}
                isAmazonProduct={isAmazonProduct}
                isLoggedIn={isLoggedIn}
                isOnSale={buyOption.is_on_sale}
                isSavedForLater={buyArea.is_saved_for_later}
                lastChecked={buyOption.last_checked_at}
                leadLabel={buyOption.lead_label}
                leadUrl={buyOption.lead_url}
                priceWithCurrencySymbol={buyOption.price_with_currency_symbol}
                productId={productHeader.product_id}
                promotion={promotion}
                retailerName={buyOption.retailer_name}
                shippingCostLabel={buyOption.shipping_cost_label}
            />
            {isComparePricesEnabled && (
                <button
                    className={style.comparePrices}
                    onClick={() => setComparePriceOpen(!comparePriceOpen)}
                >
                    <span className={style.comparePricesText}>{comparePriceText}</span>
                    <SVGIcon name="round-chevron" />
                </button>
            )}
            {buyOption.free_shipping && (
                <div className={style.shippingArea}>
                    <h4 className={style.shippingTitle}>
                        {gettext("in_stock_product.shipping_returns_label")}
                    </h4>
                    <Shipping
                        freeShippingUrl={buyOption.free_shipping.url}
                        isSavedForLater={buyArea.is_saved_for_later}
                        leadUrl={buyOption.lead_url}
                        message={buyOption.free_shipping.message}
                        retailerName={buyOption.retailer_name}
                    />
                </div>
            )}
            <ProductDescriptionDetails
                className={style.detailsArea}
                productDescription={productDescription.description}
                productTitle={productDescription.title}
            />
        </div>
    );
}

ProductDescription.defaultProps = {
    isAmazonProduct: false,
    isAboveFoldSaveForLaterEnabled: false,
    isComparePricesEnabled: true
};

const buyOptionPropType = PropTypes.shape({
    is_on_sale: PropTypes.bool,
    price_with_currency_symbol: PropTypes.string.isRequired,
    shipping_cost_label: PropTypes.string,
    lead_url: PropTypes.string.isRequired,
    lead_label: PropTypes.string.isRequired,
    is_saved_for_later: PropTypes.bool,
    sale_price_with_currency_symbol_html: PropTypes.string.isRequired,
    retailer_name: PropTypes.string,
    retailer_slug: PropTypes.string,
    free_shipping: PropTypes.shape({
        url: PropTypes.string,
        message: PropTypes.string.isRequired
    }),
    last_checked_at: PropTypes.string,
    date_updated: PropTypes.string,
    retailer_tc_link: PropTypes.string,
    retailer_tc_link_text: PropTypes.string,
    terms_and_conditions_link: PropTypes.string,
    promotions: PropTypes.arrayOf(
        PropTypes.shape({
            message: PropTypes.string.isRequired,
            url: PropTypes.string
        })
    )
});

ProductDescription.propTypes = {
    productHeader: PropTypes.shape({
        is_owner_active: PropTypes.bool,
        designer_url: PropTypes.string,
        product_id: PropTypes.string.isRequired,
        owner_name: PropTypes.string.isRequired,
        short_description: PropTypes.string.isRequired
    }).isRequired,
    absoluteUrl: PropTypes.string.isRequired,
    monopriceBuyArea: PropTypes.shape({
        buy_option: buyOptionPropType,
        is_saved_for_later: PropTypes.bool
    }),
    expressCheckoutBuyArea: PropTypes.shape({
        buy_option: buyOptionPropType,
        is_saved_for_later: PropTypes.bool,
        size_guide_link: PropTypes.arrayOf(PropTypes.string),
        sizes: PropTypes.arrayOf(
            PropTypes.shape({
                option_value: {
                    link_id: PropTypes.string,
                    raw_size: PropTypes.string,
                    icon: PropTypes.bool
                },
                human_size: PropTypes.string,
                raw_size: PropTypes.string,
                min_price: PropTypes.number
            })
        )
    }),
    productDescription: PropTypes.shape({
        description: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }).isRequired,
    isAmazonProduct: PropTypes.bool,
    isComparePricesEnabled: PropTypes.bool
};

export default ProductDescription;
